import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { PanelRightClose, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AssistPanelHostProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  children: ReactNode;
}

export function AssistPanelHost({
  open,
  onOpenChange,
  title,
  description,
  defaultWidth = 480,
  minWidth = 320,
  maxWidth = 900,
  children,
}: AssistPanelHostProps) {
  const [width, setWidth] = useState(defaultWidth);
  const draggingRef = useRef(false);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    const startX = e.clientX;
    const startWidth = width;

    const onMove = (ev: MouseEvent) => {
      if (!draggingRef.current) return;
      const delta = startX - ev.clientX;
      setWidth(Math.max(minWidth, Math.min(maxWidth, startWidth + delta)));
    };

    const onUp = () => {
      draggingRef.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [maxWidth, minWidth, width]);

  if (!open) return null;

  return (
    <>
      <div
        className="w-1.5 shrink-0 cursor-col-resize bg-border transition-colors hover:bg-primary/20"
        onMouseDown={handleDragStart}
        aria-hidden="true"
      >
        <div className="flex h-full items-center justify-center">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
      <div className="flex min-h-0 shrink-0 flex-col border-l bg-background" style={{ width }}>
        <div className="flex items-start gap-2 border-b px-3 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">{title}</div>
            {description ? <div className="mt-0.5 text-xs text-muted-foreground">{description}</div> : null}
          </div>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onOpenChange(false)} title="Close assist panel">
            <PanelRightClose className="h-4 w-4" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">{children}</div>
      </div>
    </>
  );
}
